import { useState } from 'react'
import { FiX } from 'react-icons/fi'

import { reportSession } from '@/api/agent'

const REPORT_TYPES = [
  { label: '违法违规', value: 1 },
  { label: '色情低俗', value: 2 },
  { label: '虚假信息', value: 3 },
  { label: '侵权内容', value: 4 },
  { label: '其他', value: 5 }
]

interface ReportModalProps {
  isOpen: boolean
  sessionId: string | null
  onClose: () => void
}

export default function ReportModal({ isOpen, sessionId, onClose }: ReportModalProps) {
  const [selectedType, setSelectedType] = useState<number | null>(null)
  const [reason, setReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async () => {
    if (!sessionId || selectedType === null) return

    setIsSubmitting(true)
    try {
      await reportSession({
        sessionId,
        reportType: selectedType,
        reason: reason.trim()
      })
      // Reset and close on success
      setSelectedType(null)
      setReason('')
      onClose()
    } catch (error) {
      console.error('Failed to report session', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setSelectedType(null)
    setReason('')
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm transition-opacity duration-300">
      <div className="relative w-full max-w-[480px] overflow-hidden rounded-2xl bg-white p-6 shadow-2xl transition-all duration-300 animate-in fade-in zoom-in-95">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-gray-900">举报</h2>
          <button onClick={handleClose} className="rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
            <FiX className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          {REPORT_TYPES.map((type, index) => (
            <button
              key={index}
              onClick={() => setSelectedType(type.value)}
              className={`px-4 py-2 rounded-lg text-sm transition-colors border ${
                selectedType === type.value ? 'border-blue-500 bg-blue-50 text-blue-600' : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>

        <div className="relative mb-6">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
            placeholder="我们想知道你举报的原因，你可以描述你遇到的问题。"
            className="w-full h-32 p-3 text-sm text-gray-900 bg-white border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-400"
          />
          <div className="absolute bottom-3 right-3 text-xs text-gray-400">{reason.length}/500</div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button onClick={handleClose} className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={selectedType === null || isSubmitting}
            className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
              selectedType === null || isSubmitting ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'
            }`}
          >
            {isSubmitting ? '提交中...' : '提交'}
          </button>
        </div>
      </div>
    </div>
  )
}
